/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * The "Bandwidth" section of the new-transfer wizard's source options and of the transfer edit
 * dialog: the limit outside the schedule, a schedule of business-hours windows (the generic
 * options renderer cannot edit an array of objects) and a week strip showing which limit applies
 * when. Validation mirrors the API (bandwidth.ts).
 */

import * as React from "react";
import styled from "styled-components";
import { Gauge, Plus, Trash2 } from "lucide-react";

import { T9 } from "@src/components/ui/Lists/ListTable";
import {
  Chip,
  FieldError,
  FieldHint,
  LinkButton,
  OpsInput,
  OpsSelect,
} from "@src/components/ui/OpsDialog";
import type { BandwidthDay, BandwidthRule } from "@src/@types/Cutover";

import {
  DAYS,
  DAY_LABELS,
  DEFAULT_TZ,
  MAX_RULES,
  TIME_ZONES,
  WEEKDAYS,
  WEEKEND,
  describeDays,
  formatLimit,
  isOvernight,
  isWholeDay,
  mibpsToMbit,
  newRule,
  validateBandwidth,
  weekPreview,
  zoneParts,
} from "./bandwidth";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-width: 0;
`;
const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${T9.slate200};
  .icon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: ${T9.brand50};
    color: ${T9.brand600};
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
const HeadText = styled.div`
  flex: 1;
  min-width: 0;
  .t {
    font-size: 15px;
    line-height: 20px;
    font-weight: 600;
    color: ${T9.slate900};
  }
  .s {
    font-size: 12px;
    line-height: 16px;
    color: ${T9.slate500};
  }
`;
const LimitRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px 24px;
  align-items: start;
`;
const Label = styled.div`
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  color: ${T9.slate800};
  margin-bottom: 6px;
`;
const InputUnit = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  .unit {
    font-size: 13px;
    color: ${T9.slate500};
    white-space: nowrap;
  }
`;
const Conversion = styled.div`
  font-size: 12px;
  color: ${T9.slate500};
  font-variant-numeric: tabular-nums;
  padding-top: 32px;
  white-space: nowrap;
`;
const ScheduleHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
const RuleCard = styled.div<{ invalid?: boolean }>`
  border: 1px solid ${props => (props.invalid ? "var(--t9-danger-200)" : T9.slate200)};
  border-radius: 8px;
  padding: 12px 12px 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--t9-bg-surface);
  position: relative;
  .swatch {
    position: absolute;
    left: -1px;
    top: 10px;
    bottom: 10px;
    width: 3px;
    border-radius: 0 3px 3px 0;
  }
`;
const DaysRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  .presets {
    display: flex;
    gap: 10px;
    margin-left: 8px;
    font-size: 12px;
  }
`;
const TimesRow = styled.div`
  display: grid;
  grid-template-columns: 128px 128px 140px minmax(150px, 1fr) 34px;
  gap: 10px;
  align-items: end;
  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
`;
const Small = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  span {
    font-size: 11px;
    line-height: 14px;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: ${T9.slate500};
  }
`;
const IconButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 6px;
  border: 1px solid ${T9.slate300};
  background: var(--t9-bg-surface);
  color: ${T9.slate500};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    color: var(--t9-danger-700);
    border-color: var(--t9-danger-200);
    background: var(--t9-danger-50);
  }
`;
const AddButton = styled.button`
  flex-shrink: 0;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid ${T9.slate300};
  background: var(--t9-bg-surface);
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${T9.slate700};
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${T9.slate50};
    color: ${T9.slate900};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const Empty = styled.div`
  border: 1px dashed ${T9.slate300};
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 13px;
  color: ${T9.slate500};
`;
const RuleNote = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${T9.slate500};
`;

// ------------------------------------------------------------------ week strip

const Strip = styled.div`
  border: 1px solid ${T9.slate200};
  border-radius: 8px;
  padding: 12px 14px 10px 14px;
  background: ${T9.slate50};
`;
const StripHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  .t {
    font-size: 12px;
    font-weight: 600;
    color: ${T9.slate700};
  }
  .z {
    font-size: 12px;
    color: ${T9.slate500};
  }
`;
const StripRow = styled.div`
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  height: 18px;
  margin-bottom: 4px;
  .d {
    font-size: 11px;
    font-weight: 500;
    color: ${T9.slate500};
  }
`;
const Bar = styled.div`
  position: relative;
  height: 14px;
  border-radius: 3px;
  overflow: hidden;
  background: var(--t9-bg-surface);
  box-shadow: inset 0 0 0 1px ${T9.slate200};
`;
const Seg = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
`;
const NowMark = styled.div`
  position: absolute;
  top: -2px;
  bottom: -2px;
  width: 2px;
  background: var(--t9-danger-700);
  border-radius: 1px;
`;
const Ticks = styled.div`
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  gap: 8px;
  .t {
    position: relative;
    height: 14px;
    font-size: 10px;
    color: ${T9.slate400};
    font-variant-numeric: tabular-nums;
  }
  .t span {
    position: absolute;
    transform: translateX(-50%);
  }
  .t span:first-child {
    transform: none;
  }
  .t span:last-child {
    transform: translateX(-100%);
  }
`;
const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  margin-top: 8px;
  font-size: 12px;
  color: ${T9.slate600};
  .i {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .sw {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    box-shadow: inset 0 0 0 1px rgb(15 23 42 / 0.08);
  }
`;

// The windows' limits get strong colours (lower limits first); the limit outside the windows a pale
// tint, and "unlimited" a pale green.
const LIMIT_COLOURS = ["var(--t9-primary-hover)", "var(--t9-accent-500)", "var(--t9-accent-500)", "var(--t9-series-4)", "var(--t9-series-3)", "var(--t9-brand-500)", "var(--t9-series-5)", "var(--t9-brand-300)"];
const UNLIMITED_COLOUR = "var(--t9-info-50)";
const DEFAULT_COLOUR = "var(--t9-brand-100)";

export const colourFor = (limit: number, limits: number[], defaultLimit = 0) => {
  if (!limit) return UNLIMITED_COLOUR;
  if (limit === defaultLimit) return DEFAULT_COLOUR;
  const i = limits.filter(l => l && l !== defaultLimit).indexOf(limit);
  return LIMIT_COLOURS[(i < 0 ? 0 : i) % LIMIT_COLOURS.length];
};

type PreviewProps = {
  limit: number;
  schedule: BandwidthRule[];
  tz: string;
  now?: number;
};
export const WeekPreview = ({ limit, schedule, tz, now = Date.now() }: PreviewProps) => {
  const rows = React.useMemo(() => weekPreview(limit, schedule, tz, now), [limit, JSON.stringify(schedule), tz, Math.floor(now / 600000)]);
  const limits = Array.from(new Set(rows.flat().map(s => s.limit))).sort((a, b) => a - b);
  const today = zoneParts(tz, now);
  return (
    <Strip data-test-id="bandwidth-preview">
      <StripHead>
        <span className="t">When each limit applies this week</span>
        <span className="z">Shown in {tz}</span>
      </StripHead>
      {rows.map((row, d) => (
        <StripRow key={DAYS[d]}>
          <span className="d">{DAY_LABELS[DAYS[d]]}</span>
          <Bar>
            {row.map(s => (
              <Seg
                key={s.start}
                title={`${DAY_LABELS[DAYS[d]]} ${hhmm(s.start)}–${hhmm(s.end)}: ${formatLimit(s.limit)}${s.rule === null ? " (default)" : ` (window ${s.rule + 1})`}`}
                style={{
                  left: `${(s.start / 1440) * 100}%`,
                  width: `${((s.end - s.start) / 1440) * 100}%`,
                  background: colourFor(s.limit, limits, Number(limit) || 0),
                }}
              />
            ))}
            {d === today.day ? <NowMark style={{ left: `${(today.minute / 1440) * 100}%` }} title="Now" /> : null}
          </Bar>
        </StripRow>
      ))}
      <Ticks>
        <span />
        <div className="t">
          {[0, 6, 12, 18, 24].map(h => (
            <span key={h} style={{ left: `${(h / 24) * 100}%` }}>
              {String(h).padStart(2, "0")}:00
            </span>
          ))}
        </div>
      </Ticks>
      <Legend>
        {limits.map(l => (
          <span className="i" key={l}>
            <span className="sw" style={{ background: colourFor(l, limits, Number(limit) || 0) }} />
            {formatLimit(l)}
            {l === (Number(limit) || 0) ? <span style={{ color: T9.slate400 }}>· outside the windows</span> : null}
          </span>
        ))}
        <span className="i">
          <span className="sw" style={{ background: "var(--t9-danger-700)", width: 2 }} />
          Now
        </span>
      </Legend>
    </Strip>
  );
};

const hhmm = (m: number) => `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

// ------------------------------------------------------------------ editor

type Props = {
  limit: number | string;
  schedule: BandwidthRule[];
  onChange: (value: { limit: number | string; schedule: BandwidthRule[] }) => void;
  /** Draw the section heading (the wizard); the edit dialog has its own tab. */
  showHeading?: boolean;
  defaultTz?: string;
  now?: number;
};

const BandwidthEditor = (props: Props) => {
  const { schedule } = props;
  const errors = validateBandwidth(props.limit, schedule);
  const numericLimit = Number(props.limit) || 0;
  const zones = Array.from(
    new Set([...TIME_ZONES, ...schedule.map(r => r.tz || "UTC"), props.defaultTz || DEFAULT_TZ].filter(Boolean)),
  );
  const displayTz = schedule[0]?.tz || props.defaultTz || DEFAULT_TZ;
  const validLimits = Array.from(new Set([numericLimit, ...schedule.map(r => Number(r.limit_mibps) || 0)])).sort((a, b) => a - b);

  const setRule = (index: number, changes: Partial<BandwidthRule>) =>
    props.onChange({
      limit: props.limit,
      schedule: schedule.map((r, i) => (i === index ? { ...r, ...changes } : r)),
    });
  const toggleDay = (index: number, day: BandwidthDay) => {
    const current = schedule[index].days && schedule[index].days!.length ? schedule[index].days! : [...DAYS];
    const next = current.includes(day) ? current.filter(d => d !== day) : DAYS.filter(d => d === day || current.includes(d));
    setRule(index, { days: next });
  };

  return (
    <Wrapper data-test-id="bandwidthEditor">
      {props.showHeading !== false ? (
        <Head>
          <span className="icon">
            <Gauge size={16} />
          </span>
          <HeadText>
            <div className="t">Bandwidth</div>
            <div className="s">
              Cap how fast this transfer reads disk data from the source. All its VMs and disks share the limit; a change
              applies from the next sync.
            </div>
          </HeadText>
        </Head>
      ) : null}

      <LimitRow>
        <div>
          <Label>Bandwidth limit</Label>
          <InputUnit>
            <OpsInput
              type="number"
              min={0}
              max={100000}
              step={1}
              style={{ width: 140 }}
              value={props.limit}
              invalid={Boolean(errors.limit)}
              aria-label="Bandwidth limit in MiB/s"
              data-test-id="bandwidth-limit"
              onChange={e => props.onChange({ limit: e.target.value, schedule })}
            />
            <span className="unit">MiB/s</span>
          </InputUnit>
          {errors.limit ? (
            <FieldError>{errors.limit}</FieldError>
          ) : (
            <FieldHint>0 means unlimited. Applies outside the schedule's windows.</FieldHint>
          )}
        </div>
        <Conversion>{numericLimit ? `≈ ${Math.round(mibpsToMbit(numericLimit))} Mbit/s` : "Unlimited"}</Conversion>
      </LimitRow>

      <div>
        <ScheduleHead>
          <div>
            <Label style={{ marginBottom: 2 }}>Business-hours schedule</Label>
            <FieldHint>
              Windows with their own limit. A window whose end is before its start runs overnight; when windows overlap,
              the narrowest wins.
            </FieldHint>
          </div>
          <AddButton
            type="button"
            disabled={schedule.length >= MAX_RULES}
            title={schedule.length >= MAX_RULES ? `At most ${MAX_RULES} windows` : undefined}
            onClick={() =>
              props.onChange({ limit: props.limit, schedule: [...schedule, newRule(schedule[schedule.length - 1]?.tz || props.defaultTz || DEFAULT_TZ)] })
            }
            data-test-id="bandwidth-add"
          >
            <Plus size={14} />
            Add window
          </AddButton>
        </ScheduleHead>
        {errors.schedule ? <FieldError>{errors.schedule}</FieldError> : null}
      </div>

      {schedule.length === 0 ? (
        <Empty>No windows: the limit above applies all the time.</Empty>
      ) : (
        schedule.map((rule, i) => {
          const e = errors.rules[i] || {};
          const invalid = Object.keys(e).length > 0;
          const days = rule.days && rule.days.length ? rule.days : rule.days ? [] : DAYS;
          return (
            <RuleCard key={i} invalid={invalid} data-test-id="bandwidth-rule">
              <span className="swatch" style={{ background: colourFor(Number(rule.limit_mibps) || 0, validLimits, numericLimit) }} />
              <DaysRow>
                {DAYS.map(d => (
                  <Chip
                    key={d}
                    type="button"
                    selected={days.includes(d)}
                    aria-pressed={days.includes(d)}
                    onClick={() => toggleDay(i, d)}
                  >
                    {DAY_LABELS[d]}
                  </Chip>
                ))}
                <span className="presets">
                  <LinkButton type="button" onClick={() => setRule(i, { days: [...WEEKDAYS] })}>
                    Weekdays
                  </LinkButton>
                  <LinkButton type="button" onClick={() => setRule(i, { days: [...WEEKEND] })}>
                    Weekend
                  </LinkButton>
                  <LinkButton type="button" onClick={() => setRule(i, { days: [...DAYS] })}>
                    Every day
                  </LinkButton>
                </span>
              </DaysRow>
              <TimesRow>
                <Small>
                  <span>Start</span>
                  <OpsInput
                    type="time"
                    value={rule.start}
                    invalid={Boolean(e.start)}
                    onChange={ev => setRule(i, { start: ev.target.value })}
                  />
                </Small>
                <Small>
                  <span>End</span>
                  <OpsInput type="time" value={rule.end} invalid={Boolean(e.end)} onChange={ev => setRule(i, { end: ev.target.value })} />
                </Small>
                <Small>
                  <span>Limit (MiB/s)</span>
                  <OpsInput
                    type="number"
                    min={0}
                    max={100000}
                    value={rule.limit_mibps as any}
                    invalid={Boolean(e.limit)}
                    onChange={ev => setRule(i, { limit_mibps: ev.target.value === "" ? ("" as any) : Number(ev.target.value) })}
                  />
                </Small>
                <Small>
                  <span>Time zone</span>
                  <OpsSelect value={rule.tz || "UTC"} invalid={Boolean(e.tz)} onChange={ev => setRule(i, { tz: ev.target.value })}>
                    {zones.map(z => (
                      <option key={z} value={z}>
                        {z}
                      </option>
                    ))}
                  </OpsSelect>
                </Small>
                <IconButton
                  type="button"
                  aria-label={`Remove window ${i + 1}`}
                  title="Remove this window"
                  onClick={() => props.onChange({ limit: props.limit, schedule: schedule.filter((_, j) => j !== i) })}
                >
                  <Trash2 size={15} />
                </IconButton>
              </TimesRow>
              {invalid ? (
                <FieldError>{[e.days, e.start && `Start: ${e.start}`, e.end && `End: ${e.end}`, e.limit, e.tz].filter(Boolean).join(" ")}</FieldError>
              ) : (
                <RuleNote>
                  {describeDays(days.length ? days : undefined)}{" "}
                  {isWholeDay(rule)
                    ? "all day (start and end are the same)"
                    : `${rule.start}–${rule.end}${isOvernight(rule) ? ", runs overnight into the next day" : ""}`}
                  : {formatLimit(Number(rule.limit_mibps))}
                  {Number(rule.limit_mibps) ? ` (≈ ${Math.round(mibpsToMbit(Number(rule.limit_mibps)))} Mbit/s)` : ""}
                </RuleNote>
              )}
            </RuleCard>
          );
        })
      )}

      <WeekPreview limit={numericLimit} schedule={schedule} tz={displayTz} now={props.now} />
    </Wrapper>
  );
};

export default BandwidthEditor;
